import React from 'react';
import MusicPlayer from '../components/MusicPlayer';
import Footer from '../components/Footer';

export default function About() {
  return (
    <main className="bg-background-dark text-white font-sans overflow-x-hidden">
      <MusicPlayer />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            alt="Artist performing on stage"
            className="w-full h-full object-cover opacity-60"
            src="/images/about_hero.png"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-24 pt-48 md:pt-24">
          <h1 className="text-8xl md:text-[12rem] font-serif leading-none tracking-tighter mb-8 text-white">
            About
          </h1>
          <p className="text-xl opacity-80 mb-10 max-w-3xl mx-auto font-serif italic leading-relaxed text-white">
            "Music is the bridge between the conscious and unconscious mind. I build those bridges."
          </p>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-32 px-8 md:px-32 bg-black">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <div className="relative">
            <img
              alt="Artist profile"
              className="w-full h-[650px] object-cover grayscale brightness-75 border border-white/10"
              src="/images/img6.jpg"
            />
            <div className="absolute -top-4 -left-4 w-full h-full border border-white/5 z-[-1]"></div>
          </div>

          <div className="pl-0 md:pl-10">
            <span className="text-[10px] uppercase tracking-[0.5em] mb-8 block text-body-grey">Biography</span>
            <h2 className="font-display text-6xl md:text-8xl mb-12 italic leading-none font-bold text-primary">
              The Journey <br />Begins.
            </h2>

            <div className="space-y-8 text-base leading-relaxed font-light opacity-60 max-w-lg">
              <p>
                TheRealestt is an artist from South-South Nigeria, known for his creativity and distinctive sound. Blending Afroswing, Afro-Caribbean, and UK rap, he crafts music that expresses his life, style, and experiences.
              </p>
              <p>
                Every track is a reflection of his journey, bringing a unique voice to the global music scene. His sound transcends borders, weaving together the rhythmic pulse of Afroswing with the melodic richness of Afro-Caribbean influences and the raw authenticity of UK rap.
              </p>
              <p>
                From the vibrant streets of South-South Nigeria to the international stage, TheRealestt's music serves as a bridge between cultures, telling stories that resonate with listeners worldwide while staying true to his roots and personal experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Artist Images Gallery */}
      <section className="py-32 bg-zinc-950 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h3 className="font-serif text-5xl italic tracking-tight mb-6 text-primary">Visual Stories</h3>
            <p className="text-body-grey font-serif italic text-lg opacity-80 max-w-2xl mx-auto">
              Moments captured between the music—behind the scenes, on stage, and in the creative process.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4 h-[800px]">
            {/* Large featured image */}
            <div className="col-span-12 md:col-span-6 row-span-2 relative overflow-hidden group">
              <img
                alt="Artist performing on stage"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="/images/img8.png"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-6 left-6">
                  <p className="text-white text-sm font-serif italic">Live at The Underground, 2024</p>
                </div>
              </div>
            </div>

            {/* Studio shot */}
            <div className="col-span-6 md:col-span-3 relative overflow-hidden group">
              <img
                alt="Artist in recording studio"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=400&h=300&fit=crop&crop=faces"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
            </div>

            {/* Portrait shot */}
            <div className="col-span-6 md:col-span-3 relative overflow-hidden group">
              <img
                alt="Artist portrait"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="/images/img9.png"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
            </div>

            {/* Creative process */}
            <div className="col-span-4 md:col-span-2 relative overflow-hidden group">
              <img
                alt="Writing process"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="/images/img2.jpg"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
            </div>

            {/* Behind the scenes */}
            <div className="col-span-8 md:col-span-4 relative overflow-hidden group">
              <img
                alt="Behind the scenes"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&h=400&fit=crop&crop=center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-6 left-6">
                  <p className="text-white text-sm font-serif italic">Creating "Midnight Theory"</p>
                </div>
              </div>
            </div>

            {/* Candid moment */}
            <div className="col-span-6 md:col-span-3 relative overflow-hidden group">
              <img
                alt="Candid moment"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=300&fit=crop&crop=faces"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
            </div>

            {/* Performance shot */}
            <div className="col-span-6 md:col-span-3 relative overflow-hidden group">
              <img
                alt="Live performance"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                src="https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&h=300&fit=crop&crop=center"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
            </div>
          </div>

        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 bg-zinc-950 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-serif text-4xl italic tracking-tight mb-12 text-primary">Creative Philosophy</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-20">
            <div className="text-center">
              <h4 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-4 text-white">Authenticity</h4>
              <p className="text-sm text-body-grey font-serif italic leading-relaxed">
                Every note, every word, every silence carries the weight of genuine experience. No facades, no pretense—just truth.
              </p>
            </div>
            <div className="text-center">
              <h4 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-4 text-white">Innovation</h4>
              <p className="text-sm text-body-grey font-serif italic leading-relaxed">
                Pushing boundaries while respecting tradition. Creating new sounds from familiar emotions.
              </p>
            </div>
            <div className="text-center">
              <h4 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-4 text-white">Connection</h4>
              <p className="text-sm text-body-grey font-serif italic leading-relaxed">
                Music as a bridge between souls. Every song is an invitation to feel, to remember, to heal.
              </p>
            </div>
          </div>

          <blockquote className="text-2xl font-serif italic text-primary leading-relaxed max-w-3xl mx-auto">
            "I don't make music for the charts or the critics. I make music for the moments when you need something real to hold onto. Every track is a reflection of my journey, bringing my unique voice to the global music scene."
          </blockquote>
        </div>
      </section>

      <Footer />
    </main>
  );
}